import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { Spinner } from '../../ui/Spinner';
import { Alert } from '../../ui/Alert';
import { FollowUpForm } from './FollowUpForm';
import { FollowUpRow } from './FollowUpRow';
import { followUpService } from '../../../services/followUpService';

const byDue = (a, b) => new Date(a.due_date) - new Date(b.due_date);
const byDone = (a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0);

export const PersonFollowUps = ({ user, contactId, connectionId, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionError, setActionError] = useState('');
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    let alive = true;
    followUpService
      .listForPerson({ userId: user.id, contactId, connectionId })
      .then((d) => alive && setItems(d))
      .catch((e) => alive && setLoadError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user.id, contactId, connectionId]);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };
  const replace = (row) => setItems((prev) => prev.map((i) => (i.id === row.id ? row : i)));
  const changed = () => onChanged && onChanged();

  const guard = async (fn) => {
    setActionError('');
    try {
      await fn();
    } catch (err) {
      setActionError(err.message);
      throw err;
    }
  };

  const handleCreate = async ({ day, note }) => {
    const created = await followUpService.create({ userId: user.id, contactId, connectionId, day, note });
    setItems((prev) => [created, ...prev]);
    setActionError('');
    flash('Follow-up added.');
    changed();
  };

  const handleSave = (item, v) => guard(async () => {
    replace(await followUpService.update(item.id, user.id, v));
    flash('Follow-up updated.');
    changed();
  });
  const handleComplete = (item, n) => guard(async () => {
    replace(await followUpService.complete(item.id, user.id, n));
    flash('Follow-up completed.');
    changed();
  });
  const handleReopen = (item) => guard(async () => {
    replace(await followUpService.reopen(item.id, user.id));
    changed();
  }).catch(() => {});
  const handleDelete = (item) => guard(async () => {
    if (!window.confirm('Delete this follow-up? This cannot be undone.')) return;
    await followUpService.remove(item.id, user.id);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    changed();
  }).catch(() => {});

  const open = items.filter((i) => !i.is_completed).sort(byDue);
  const done = items.filter((i) => i.is_completed).sort(byDone);
  const rowProps = { onSave: handleSave, onComplete: handleComplete, onReopen: handleReopen, onDelete: handleDelete };

  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-4">
      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center">
        <SafeIcon icon={FiIcons.FiCalendar} className="mr-1.5" /> Follow-ups
        {open.length > 0 && <span className="ml-2 bg-slate-100 text-slate-600 py-0.5 px-2 rounded-full normal-case">{open.length} open</span>}
      </h4>
      {success && <Alert variant="success" message={success} />}
      {actionError && <Alert variant="error" message={actionError} />}
      {loadError && <Alert variant="error" title="Could not load follow-ups" message={loadError} />}
      {loading ? (
        <div className="flex justify-center py-4"><Spinner size="sm" /></div>
      ) : (
        <>
          {open.length === 0 ? (
            <p className="text-sm text-slate-500">No open follow-ups for this person.</p>
          ) : (
            <div className="space-y-2">
              {open.map((i) => <FollowUpRow key={i.id} item={i} {...rowProps} />)}
            </div>
          )}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-slate-500 mb-2">Add a follow-up</p>
            <FollowUpForm resetOnSuccess onSubmit={handleCreate} />
          </div>
          {done.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <button type="button" onClick={() => setShowDone((v) => !v)} className="text-xs font-bold text-slate-500 hover:text-slate-900">
                {showDone ? 'Hide' : 'Show'} completed ({done.length})
              </button>
              {showDone && (
                <div className="space-y-2 mt-2">
                  {done.map((i) => <FollowUpRow key={i.id} item={i} {...rowProps} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};